import * as THREE from 'three';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

/**
 * The detailed (Meshy) car body file written by scripts/build-car-model.ts and read by meshyCar.ts:
 *
 *   u32 magic, u32 header length, JSON header (CarModelHeader, space-padded to 4 bytes), then the meshes.
 *
 * Each mesh: position f32×3, normal i8×4 (normalised), uv u16×2 (normalised), colour u8×4 (normalised),
 * surf u8×4 (paint slot, roughness×255, metalness×255, channel), index u16 (u32 when `index32`); every
 * stream padded to 4 bytes. Local frame as meshes.ts: +Z forward, +X the car's left, ground at y = 0.
 */
export const CAR_MODEL_MAGIC = 0x52414342; // 'BCAR'
export const CAR_MODEL_VERSION = 1;
/**
 * Version 2 (what ships, written from version 1 by scripts/compress-models.mjs): the same six streams per mesh,
 * each meshopt-encoded (EXT_meshopt_compression's codec; `streams` gives each one's byte length and mode), the
 * positions as 16 bits over the mesh's box (`qpos`, a tenth of a millimetre on a car), and the textures as KTX2.
 * The other streams decode to the version-1 bytes (the index codec may rotate a triangle's corners).
 */
export const CAR_MODEL_VERSION_COMPRESSED = 2;
const MESHOPT_MODES = ['ATTRIBUTES', 'TRIANGLES', 'INDICES'] as const;

/** Resolves once the meshopt decoder (WASM) can decode version-2 files: await it before parseCarModel. */
export const carModelDecoderReady: Promise<void> = MeshoptDecoder.ready;

export interface CarModelMesh {
  offset: number;
  vertices: number;
  indices: number;
  index32: boolean;
  /** Version 2: [encoded byte length, mode (0 attributes, 1 triangles, 2 indices)] of each of the six streams. */
  streams?: [number, number][];
  /** Version 2: positions are uint16 × 4 over this box: min x, y, z, then the extent along x, y, z. */
  qpos?: [number, number, number, number, number, number];
}

export interface CarModelLod {
  body: CarModelMesh;
  glass: CarModelMesh;
}

export interface CarModelHeader {
  magic: number;
  version: number;
  id: string;
  /** The spec the model was fitted to (wheels, axles, roof). */
  spec: string;
  lods: { high: CarModelLod; mid: CarModelLod };
  anchors: {
    /** Lid hinges (z, y); null where the car has no such lid (as VehicleParts). */
    bonnetHinge: [number, number] | null;
    bootHinge: [number, number] | null;
    /** Bumper centre (z, y) and half width, as VehicleParts. */
    bumperF: [number, number, number];
    bumperR: [number, number, number];
    /** Base of the side glass; top of the side glass + a little (the roof line: vinyl roof, gang livery). */
    beltY: number;
    roofLine: number;
    roofY: number;
    zFront: number;
    zRear: number;
    /** z of the windscreen's base and of the rear screen's base (the cabin parts are fitted between them). */
    screenF: number;
    screenR: number;
    /** Plate centres (x, y, z), where the procedural plate faces go. */
    plateF: [number, number, number];
    plateR: [number, number, number];
  };
  /**
   * [base colour, mask] per LOD (files next to the .bin). HIGH keeps Meshy's own atlas (1024² / 512² mask); the MID
   * body has a rebaked 512² atlas of its own (its triangles no longer follow Meshy's islands).
   */
  textures: { high: [string, string]; mid: [string, string] };
}

export interface ParsedCarModel {
  header: CarModelHeader;
  high: { body: THREE.BufferGeometry; glass: THREE.BufferGeometry };
  mid: { body: THREE.BufferGeometry; glass: THREE.BufferGeometry };
}

/** Float copies of every stream (the runtime merges these with the procedural parts, which are float). */
function readMesh(buf: ArrayBuffer, base: number, m: CarModelMesh): THREE.BufferGeometry {
  const n = m.vertices;
  const pad = (b: number) => (b + 3) & ~3;
  const isz = m.index32 ? 4 : 2;
  // Version 2 stores positions as uint16 × 4 over the mesh's box (`qpos`).
  const sizes = [m.qpos ? 8 : 12, 4, 4, 4, 4, isz];
  let o = base + m.offset;
  // The six streams as version-1 bytes: sliced (v1) or decoded (v2).
  const s = sizes.map((size, k) => {
    const count = k === 5 ? m.indices : n;
    if (!m.streams) {
      const b = buf.slice(o, o + count * size);
      o += pad(count * size);
      return b;
    }
    const [len, mode] = m.streams[k]!;
    const out = new Uint8Array(count * size);
    MeshoptDecoder.decodeGltfBuffer(out, count, size, new Uint8Array(buf, o, len), MESHOPT_MODES[mode]!);
    o += pad(len);
    return out.buffer;
  });
  let pos: Float32Array;
  if (m.qpos) {
    const q16 = new Uint16Array(s[0]!);
    const qp = m.qpos;
    pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) pos[i * 3 + k] = qp[k]! + (q16[i * 4 + k]! / 65535) * qp[3 + k]!;
  } else pos = new Float32Array(s[0]!);
  const nrm8 = new Int8Array(s[1]!);
  const uv16 = new Uint16Array(s[2]!);
  const col8 = new Uint8Array(s[3]!);
  const surf8 = new Uint8Array(s[4]!);
  const idx = m.index32 ? new Uint32Array(s[5]!) : new Uint16Array(s[5]!);
  const nrm = new Float32Array(n * 3);
  const uv = new Float32Array(n * 2);
  const col = new Float32Array(n * 3);
  const surf = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    let x = nrm8[i * 4]! / 127;
    let y = nrm8[i * 4 + 1]! / 127;
    let z = nrm8[i * 4 + 2]! / 127;
    const l = Math.hypot(x, y, z) || 1;
    x /= l;
    y /= l;
    z /= l;
    nrm[i * 3] = x;
    nrm[i * 3 + 1] = y;
    nrm[i * 3 + 2] = z;
    uv[i * 2] = uv16[i * 2]! / 65535;
    uv[i * 2 + 1] = uv16[i * 2 + 1]! / 65535;
    for (let k = 0; k < 3; k++) col[i * 3 + k] = col8[i * 4 + k]! / 255;
    surf[i * 4] = surf8[i * 4]!;
    surf[i * 4 + 1] = surf8[i * 4 + 1]! / 255;
    surf[i * 4 + 2] = surf8[i * 4 + 2]! / 255;
    surf[i * 4 + 3] = surf8[i * 4 + 3]!;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('surf', new THREE.BufferAttribute(surf, 4));
  g.setIndex(new THREE.BufferAttribute(Uint32Array.from(idx), 1));
  return g;
}

export function parseCarModel(buf: ArrayBuffer): ParsedCarModel {
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== CAR_MODEL_MAGIC) throw new Error('not a car model');
  const hl = dv.getUint32(4, true);
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, hl))) as CarModelHeader;
  if (header.version !== CAR_MODEL_VERSION && header.version !== CAR_MODEL_VERSION_COMPRESSED)
    throw new Error(`car model version ${header.version}, want ${CAR_MODEL_VERSION} or ${CAR_MODEL_VERSION_COMPRESSED}`);
  const base = 8 + hl;
  const lod = (l: CarModelLod) => ({ body: readMesh(buf, base, l.body), glass: readMesh(buf, base, l.glass) });
  return { header, high: lod(header.lods.high), mid: lod(header.lods.mid) };
}
